import { useState, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  Radio, 
  Users, 
  Activity, 
  Clock, 
  CheckCircle2, 
  XCircle,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API ? API.replace('http', 'ws').replace('https', 'wss') : 'ws://localhost:8001';

export default function LiveInterviews() {
  const [activeInterviews, setActiveInterviews] = useState([]);
  const [connected, setConnected] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (!user) return;

    // Use current location host for WS URL to handle all environments (localhost, preview, prod)
    // If protocol is https, use wss, otherwise ws
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host; // Includes port if any
    
    // In preview env, REACT_APP_BACKEND_URL might be different from window.location
    // But usually we want to connect to the backend.
    // If backend is on same domain (via ingress), use window.location.
    // If backend is separate, parse API URL.
    
    let wsUrl = '';
    if (API) {
        // Handle full URL in API env var
        try {
            const apiUrl = new URL(API);
            const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
            wsUrl = `${wsProtocol}//${apiUrl.host}`;
        } catch (e) {
            // If API is relative or invalid
            wsUrl = `${protocol}//${host}`;
        }
    } else {
        wsUrl = 'ws://localhost:8001';
    }

    console.log("Connecting to WS:", `${wsUrl}/api/ws/monitor`);
    const ws = new WebSocket(`${wsUrl}/api/ws/monitor?user_id=${user.user_id}`);

    ws.onopen = () => {
      console.log('Connected to Live Monitor');
      setConnected(true);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWsMessage(data);
    };

    ws.onclose = (e) => {
      console.log('Disconnected from Live Monitor', e.code, e.reason);
      setConnected(false);
      // Auto-reconnect after 3s
      setTimeout(() => {
          if (user) { 
              // Trigger re-render to reconnect or just let effect handle it if we toggle user? 
              // Better to just have a reconnect state or rely on refresh for now.
              // Simple refresh logic:
              console.log("Attempting reconnect...");
              // We can't force effect re-run easily without state change.
          }
      }, 3000);
    };

    return () => {
      ws.close();
    };
  }, [user]);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (res.ok) setUser(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const handleWsMessage = (data) => {
    // Expected data types: 'init', 'interview_started', 'interview_update', 'interview_completed'
    if (data.type === 'init') {
      setActiveInterviews(data.interviews || []);
    } else if (data.type === 'interview_started') {
      setActiveInterviews(prev => [data.interview, ...prev]);
      toast.info(`Nouvel entretien démarré : ${data.interview.candidate_name}`);
    } else if (data.type === 'transcript_update' || data.type === 'interview_update') {
      setActiveInterviews(prev => prev.map(i => 
        i.session_id === data.session_id ? { ...i, ...data.interview } : i
      ));
    } else if (data.type === 'interview_completed') {
      setActiveInterviews(prev => prev.map(i => 
        i.session_id === data.session_id ? { ...i, status: 'completed', ...data.interview } : i
      ));
      toast.success(`Entretien terminé : ${data.interview.candidate_name}`);
    } else if (data.type === 'interview_interrupted') {
       setActiveInterviews(prev => prev.map(i => 
        i.session_id === data.session_id ? { ...i, status: 'interrupted', ...data.interview } : i
      ));
      toast.warning(`Entretien interrompu : ${data.interview.candidate_name}`);
    }
  };

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-display font-bold text-3xl text-ink">Live Monitor</h1>
              {connected ? (
                <Badge className="bg-red-500 hover:bg-red-600 animate-pulse gap-1 pl-1.5">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  LIVE
                </Badge>
              ) : (
                <Badge variant="outline" className="text-zinc-400">Déconnecté</Badge>
              )}
            </div>
            <p className="text-ink-muted">Suivez les entretiens en temps réel.</p>
          </div>
        </div>

        {activeInterviews.length === 0 ? (
          <Card className="card-flat border-dashed">
            <CardContent className="py-16 text-center">
              <Activity className="w-12 h-12 text-ink-muted mx-auto mb-4 opacity-50" />
              <h3 className="font-display font-semibold text-xl text-ink mb-2">
                Aucun entretien en cours
              </h3>
              <p className="text-ink-muted">
                Les entretiens actifs apparaîtront ici automatiquement.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {activeInterviews.map((interview) => (
              <Card key={interview.session_id} className={`card-flat overflow-hidden border-l-4 ${
                interview.status === 'in_progress' ? 'border-l-blue-500' :
                interview.status === 'completed' ? 'border-l-green-500' : 'border-l-red-500'
              }`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center font-bold text-lg text-zinc-500">
                        {interview.candidate_name?.charAt(0)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{interview.candidate_name}</CardTitle>
                        <p className="text-sm text-ink-muted">{interview.job_title}</p>
                      </div>
                    </div>
                    <Badge variant={interview.status === 'in_progress' ? 'default' : 'secondary'} className={
                      interview.status === 'in_progress' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' : 
                      interview.status === 'completed' ? 'bg-green-100 text-green-700' : 
                      'bg-red-100 text-red-700'
                    }>
                      {interview.status === 'in_progress' ? (
                        <span className="flex items-center gap-1"><Radio className="w-3 h-3 animate-pulse" /> En cours</span>
                      ) : interview.status === 'completed' ? (
                        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Terminé</span>
                      ) : (
                        <span className="flex items-center gap-1"><XCircle className="w-3 h-3" /> Interrompu</span>
                      )}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Progress */}
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-ink-muted">Progression</span>
                        <span className="font-medium">Q{interview.current_question_index || 0} / {interview.total_questions || '?'}</span>
                      </div>
                      <div className="h-2 w-full bg-zinc-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${Math.min(100, ((interview.current_question_index || 0) / (interview.total_questions || 10)) * 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* Live Transcript Snippet */}
                    <div className="bg-zinc-50 rounded-lg p-4 font-mono text-sm max-h-40 overflow-y-auto">
                      {interview.transcript?.length > 0 ? (
                        <div className="space-y-3">
                          {interview.transcript.slice(-2).map((msg, i) => (
                            <div key={i} className="flex flex-col gap-1">
                              <p className="text-blue-600 font-bold text-xs">EMA:</p>
                              <p className="text-zinc-600 mb-2">{msg.q}</p>
                              <p className="text-green-600 font-bold text-xs">CANDIDAT:</p>
                              <p className="text-zinc-800">{msg.r || "..."}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-400 italic text-center">En attente de démarrage...</p>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center text-xs text-ink-muted">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Démarré à {new Date(interview.started_at).toLocaleTimeString()}
                      </span>
                      <span>ID: {interview.session_id.slice(0, 8)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
